/*
Copyright 2026 Integrated Tech9labs Pvt. Ltd.
This file is part of Swift and is licensed under the GNU Affero General
Public License, version 3 (see the LICENSE file at the root of the repository).
*/

import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import { ThemePalette } from "@src/components/Theme";
import Button from "@src/components/ui/Button";
import Checkbox from "@src/components/ui/Checkbox";
import TextInput from "@src/components/ui/TextInput";
import providerStore from "@src/stores/ProviderStore";
import wizardStore from "@src/stores/WizardStore";

import type { WizardData } from "@src/@types/WizardData";

/** Target options this section owns (hidden from the target options page). */
export const REMOTE_ACCESS_KEYS = ["allow_remote_access", "remote_access_cidr", "remote_access_cidrs"];

const IPV4_CIDR = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/;

/** "203.0.113.24" -> "203.0.113.24/32"; null when it is not an IPv4 address or range. */
export const normaliseCidr = (text: string): string | null => {
  const value = text.trim().includes("/") ? text.trim() : `${text.trim()}/32`;
  const m = IPV4_CIDR.exec(value);
  if (!m || m.slice(1, 5).some(n => Number(n) > 255) || Number(m[5]) > 32) {
    return null;
  }
  return value;
};

/** Adds the IPs or ranges in `text` (comma, space or semicolon separated) to `list`. */
export const addCidrs = (list: string[], text: string): { list: string[]; invalid: string[] } => {
  const invalid: string[] = [];
  const out = [...list];
  text
    .split(/[\s,;]+/)
    .filter(Boolean)
    .forEach(part => {
      const cidr = normaliseCidr(part);
      if (!cidr) {
        invalid.push(part);
      } else if (!out.includes(cidr)) {
        out.push(cidr);
      }
    });
  return { list: out, invalid };
};

/** The browser's public IP, from a public "what is my IP" service; null when it cannot be reached. */
export const detectPublicIp = async (fetcher: typeof fetch = fetch): Promise<string | null> => {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    const response = await fetcher("https://api.ipify.org?format=json", { signal: controller.signal });
    clearTimeout(timer);
    const ip = (await response.json())?.ip;
    return typeof ip === "string" && normaliseCidr(ip) ? ip : null;
  } catch {
    return null;
  }
};

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid ${ThemePalette.grayscale[2]};
  border-radius: 8px;
  padding: 14px 16px;
`;
const Row = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
`;
const Ports = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const Port = styled.span`
  font-size: 12px;
  padding: 5px 8px;
  border-radius: 4px;
  background: ${ThemePalette.grayscale[7]};
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  padding: 5px 6px 5px 10px;
  border-radius: 999px;
  border: 1px solid ${ThemePalette.grayscale[2]};
`;
const Tag = styled.em`
  font-style: normal;
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 999px;
  color: ${ThemePalette.success};
  background: ${ThemePalette.grayscale[7]};
`;
const Remove = styled.button`
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  color: ${ThemePalette.grayscale[4]};
  &:focus-visible {
    outline: 2px solid ${ThemePalette.primary};
  }
`;
const AddRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const Hint = styled.div`
  font-size: 12.5px;
  color: ${ThemePalette.grayscale[4]};
`;
const Error = styled.div`
  font-size: 12.5px;
  color: ${ThemePalette.alert};
`;
const Warning = styled.div`
  font-size: 12.5px;
  padding: 8px 10px;
  border-radius: 6px;
  color: ${ThemePalette.black};
  background: var(--t9-info-50);
`;

type Props = {
  wizardData: WizardData;
  /** Names of the selected VMs per guest OS, as resolved on the guest access page. */
  windowsVms: string[];
  linuxVms: string[];
};
type State = {
  draft: string;
  invalid: string[];
  detecting: boolean;
  detected: string | null;
};

/**
 * RDP / SSH to the migrated VMs (targets whose options have 'allow_remote_access': AWS, Azure,
 * IBM Cloud). The ports follow the guest OS of the selected VMs; the allowed ranges start with the
 * browser's public IP and take any ranges the admin adds.
 */
@observer
class WizardRemoteAccess extends React.Component<Props, State> {
  state: State = { draft: "", invalid: [], detecting: false, detected: null };

  static supported(): boolean {
    return providerStore.destinationSchema.some(f => f.name === "allow_remote_access");
  }

  get options(): any {
    return this.props.wizardData.destOptions || {};
  }

  get cidrs(): string[] {
    const list = this.options.remote_access_cidrs;
    const legacy = this.options.remote_access_cidr;
    return [...(Array.isArray(list) ? list : []), ...(legacy && !(list || []).includes(legacy) ? [legacy] : [])];
  }

  get enabled(): boolean {
    // On unless the admin turned it off.
    return this.options.allow_remote_access !== false;
  }

  set(cidrs: string[], enabled = this.enabled) {
    wizardStore.updateDestOptionsRaw("allow_remote_access", enabled);
    wizardStore.updateDestOptionsRaw("remote_access_cidrs", cidrs);
    wizardStore.updateDestOptionsRaw("remote_access_cidr", undefined);
    wizardStore.updateUrlState();
  }

  async componentDidMount() {
    if (this.options.remote_access_cidrs !== undefined) {
      this.set(this.cidrs);
      return;
    }
    // First visit: on, from this browser's public IP.
    this.set(this.cidrs, true);
    this.setState({ detecting: true });
    const ip = await detectPublicIp();
    this.setState({ detecting: false, detected: ip ? `${ip}/32` : null });
    if (ip && !this.cidrs.includes(`${ip}/32`)) {
      this.set([`${ip}/32`, ...this.cidrs]);
    }
  }

  handleAdd() {
    const { list, invalid } = addCidrs(this.cidrs, this.state.draft);
    this.set(list);
    this.setState({ draft: invalid.join(", "), invalid });
  }

  render() {
    const { windowsVms, linuxVms } = this.props;
    const what = windowsVms.length && linuxVms.length ? "RDP and SSH" : linuxVms.length ? "SSH" : "RDP";
    const cidrs = this.cidrs;
    const open = cidrs.includes("0.0.0.0/0");
    return (
      <Box>
        <Row>
          <Checkbox checked={this.enabled} onChange={checked => this.set(cidrs, checked)} />
          Allow {what} to the migrated VMs
        </Row>
        {this.enabled ? (
          <>
            <Ports>
              {windowsVms.length ? <Port>RDP · TCP 3389 · {windowsVms.join(", ")}</Port> : null}
              {linuxVms.length ? <Port>SSH · TCP 22 · {linuxVms.join(", ")}</Port> : null}
            </Ports>
            <Hint>Allowed from</Hint>
            <Chips>
              {this.state.detecting ? <Hint>Detecting your public IP…</Hint> : null}
              {cidrs.map(cidr => (
                <Chip key={cidr}>
                  {cidr}
                  {cidr === this.state.detected ? <Tag>your public IP</Tag> : null}
                  <Remove type="button" aria-label={`Remove ${cidr}`} onClick={() => this.set(cidrs.filter(c => c !== cidr))}>
                    ×
                  </Remove>
                </Chip>
              ))}
              {!this.state.detecting && !cidrs.length ? (
                <Hint>No ranges yet: SwiftIQ would allow only its own host's public IP.</Hint>
              ) : null}
            </Chips>
            <AddRow>
              <TextInput
                width="360px"
                value={this.state.draft}
                placeholder="Add an IP or range, e.g. 10.20.0.0/16 or 198.51.100.7"
                onChange={e => this.setState({ draft: e.target.value })}
                onInputKeyDown={e => {
                  if (e.key === "Enter") {
                    // Enter adds the range; it must not also press the wizard's Next.
                    e.preventDefault();
                    window.handlingEnterKey = true;
                    setTimeout(() => {
                      window.handlingEnterKey = false;
                    }, 100);
                    this.handleAdd();
                  }
                }}
              />
              <Button secondary onClick={() => this.handleAdd()} disabled={!this.state.draft.trim()}>
                Add
              </Button>
            </AddRow>
            {this.state.invalid.length ? (
              <Error>
                Not a valid IP or range: {this.state.invalid.join(", ")}. Use a form like 203.0.113.10 or 172.16.0.0/16.
              </Error>
            ) : (
              <Hint>
                {this.state.detected || this.state.detecting
                  ? "Your browser's public IP is added automatically. "
                  : "Your public IP could not be detected from this browser; add it yourself. "}
                Add office, VPN or jump-host ranges; separate several with commas. A single IP becomes /32.
              </Hint>
            )}
            {open ? (
              <Warning>0.0.0.0/0 lets anyone on the internet try {what}. Use your office or VPN ranges instead.</Warning>
            ) : null}
          </>
        ) : (
          <Hint>Off: the migrated VMs get only the security groups chosen in the target options.</Hint>
        )}
      </Box>
    );
  }
}

export default WizardRemoteAccess;
